import api from "../api";
import {subscribeToChannel, unsubscribeToChannel} from "../services/pusher";
import dates from "../services/dates";
import store from "../store";
import utils from "../services/utils";

const state = () => ({
  filters: {
    // Make sure to update resetFilters
    contacting: {
      users: [],
      campaigns: null,
      temperatures: null,
      dealProbabilityScore: null,
      sources: null,
      statuses: null,
    },
  },
  isLoadingRecords: false,
  requestTracker: 0,
  nextPageUrl: null,
  totalRecords: 0,
  records: [],
  lazyLoaderIsOn: false,
  lazyLoader: null,
  pendingLazyLoadedRecordIds: [],
  lazyLoadedRecordData: {
    /**
     *    1: record 1 data
     *    2: record 2 data
     *    3: record 3 data
     */
  },
});

const getters = {
  records(state, getters) {
    return state.records
  },
  pastDueRecords(state, getters) {
    return dates.returnItemsBeforeToday(getters.records, 'next_contact_at');
  },
  todayRecords(state, getters) {
    return dates.returnItemsForToday(getters.records, 'next_contact_at');
  },
  tomorrowRecords(state, getters) {
    return dates.returnItemsForTomorrow(getters.records, 'next_contact_at');
  },
  upcomingRecords(state, getters) {
    return dates.returnItemsForAfterTomorrow(getters.records, 'next_contact_at');
  },
  filters(state, getters) {
    return state.filters.contacting;
  },
  totalRecordsCount(state, getters) {
    return state.totalRecords
  },
  nextPageUrl(state) {
    return state.nextPageUrl
  },
  queryString(state, getters, rootGetters) {
    const users = `users=${getters.filters.users}`
    const campaigns = `campaigns=${getters.filters.campaigns}`
    const temperatures = `temperatures=${getters.filters.temperatures}`
    const dealProbabilityScore = `dealProbabilityScore=${getters.filters.dealProbabilityScore}`
    const sources= `sources=${getters.filters.sources}`
    const statuses = `statuses=${getters.filters.statuses}`

    const queryString= `${users}&${campaigns}&${temperatures}&${dealProbabilityScore}&${sources}&${statuses}`
    if (rootGetters["pipeline/selectedPipelineView"]) {
      return `${queryString}&view=${rootGetters["pipeline/selectedPipelineView"].id}`
    }

    return queryString
  },
  lazyLoadedRecordData(state) {
    return recordId => {
      return state.lazyLoadedRecordData[recordId];
    };
  },
};

const mutations = {
  setup(state, payload) {
    if (payload.length === 0) {
      state.records = []
      state.totalRecords = 0
      state.nextPageUrl = null
      return
    }

    state.records = payload.data
    state.totalRecords = payload.meta.total
    state.nextPageUrl = payload.links.next
  },
  incrementRequestTracker(state) {
    state.requestTracker = state.requestTracker + 1;
  },
  changeIsLoadingRecords(state, payload) {
    state.isLoadingRecords = payload
  },
  setOneFilter(state, [filterObject, filter, value]) {
    state.filters[filterObject][filter] = value;
    store.dispatch('updateUserAppFilter');
    store.dispatch('contactingState/loadRecords');
  },
  setUserFilters(state, filters) {
    // make sure this match the filters declared on state
    state.filters = filters;
  },
  resetFilters(state, filterObject) {
    // Make sure to update state default
    state.filters[filterObject] = {
      users: [],
      campaigns: null,
      temperatures: null,
      dealProbabilityScore: null,
      sources: null,
      statuses: null,
    };

    store.dispatch('updateUserAppFilter');
    store.dispatch('contactingState/loadRecords');
  },
  turnOnLazyLoader(state) {
    state.lazyLoaderIsOn = true;
  },
  setLazyLoader(state, lazyLoader) {
    state.lazyLoader = lazyLoader;
  },
  teardown(state) {
    state.lazyLoader = null;
    state.lazyLoaderIsOn = false;
    state.pendingLazyLoadedRecordIds = [];
    state.lazyLoadedRecordData = {};
    state.records = [];
    state.totalRecords = 0
    state.nextPageUrl = null
  },
  clearPendingLazyLoadedRecordIds(state) {
    state.pendingLazyLoadedRecordIds = [];
  },
  addRecordToPendingLazyLoadedRecords(state, recordId) {
    state.pendingLazyLoadedRecordIds = [
      recordId,
      ...state.pendingLazyLoadedRecordIds
    ];
  },
  appendLazyLoadedRecordData(state, records) {
    state.lazyLoadedRecordData = {...records, ...state.lazyLoadedRecordData};
  }
};

const actions = {
  setup({commit, state, rootState, getters, dispatch}) {
    subscribeToChannel("contactingState", rootState.market.market.id);
    dispatch('loadRecords');
  },
  tearDown({commit, state, rootState, getters}) {
    unsubscribeToChannel("contactingState", rootState.market.market.id);
    commit('teardown');
  },
  loadRecords({commit, state, rootState, getters, rootGetters, dispatch}) {
    commit('changeIsLoadingRecords', true);
    commit('setup', []);
    commit('incrementRequestTracker')
    let request = state.requestTracker;

    return api.clientStore.initContactingState(rootState.market.market.id, `?${getters.queryString}`).then(response => {
      if (request === state.requestTracker) {
        commit('setup', response.data);
        commit('changeIsLoadingRecords', false);

        commit('turnOnLazyLoader');
        dispatch('loadLazyLoadedData');
      }
    });
  },
  loadMoreRecords({commit, state, rootState, getters, rootGetters, dispatch}) {
    return api.custom.get(`${getters.nextPageUrl}&${getters.queryString}`)
      .then(response => {
        response.data.data = [...utils.clone(getters.records), ...response.data.data]
        commit('setup', response.data)

        commit('turnOnLazyLoader')
        dispatch('loadLazyLoadedData')
      })
  },
  loadLazyLoadedData({commit, state, rootState, getters, dispatch}) {
    if (state.lazyLoaderIsOn) {
      if (state.pendingLazyLoadedRecordIds.length === 0) {
        console.log('No data to load');
      } else {
        api.records.getExtraContactingDataCollection(state.pendingLazyLoadedRecordIds).then((response) => {
          commit('appendLazyLoadedRecordData', response.data);
        });
        commit('clearPendingLazyLoadedRecordIds');
      }

      let lazyLoader = setTimeout(() => {
        dispatch('loadLazyLoadedData');
      }, 1000);
      commit('setLazyLoader', lazyLoader);
    }
  }
};

const ContactingStateStore = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default ContactingStateStore;
